// ============================================
// ConverseTech Frontend - Audio Stream Player
// ============================================
// Handles smooth playback of PCM audio chunks from Gemini Live API
// Uses scheduled playback with Web Audio API for gapless audio

export interface AudioStreamPlayerCallbacks {
  onPlaybackStart?: () => void;
  onPlaybackEnd?: () => void;
  onError?: (error: Error) => void;
}

export class AudioStreamPlayer {
  private audioContext: AudioContext | null = null;
  private readonly sampleRate = 24000; // Gemini outputs at 24kHz

  // Scheduling state
  private nextStartTime = 0;
  private isPlaying = false;
  private pendingBuffers: AudioBufferSourceNode[] = [];

  // Configuration
  private readonly minBufferTime = 0.05; // 50ms minimum buffer ahead

  private callbacks: AudioStreamPlayerCallbacks;

  constructor(callbacks: AudioStreamPlayerCallbacks = {}) {
    this.callbacks = callbacks;
  }

  /**
   * Initialize audio context (must be called from user gesture)
   */
  async initialize(): Promise<void> {
    if (this.audioContext) {
      return;
    }

    try {
      // Create audio context at Gemini's output sample rate
      this.audioContext = new AudioContext({
        sampleRate: this.sampleRate,
      });

      // Resume if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

    } catch (error) {
      console.error('AudioStreamPlayer: Failed to initialize:', error);
      this.callbacks.onError?.(
        new Error(`Failed to initialize audio: ${error instanceof Error ? error.message : 'Unknown error'}`)
      );
      throw error;
    }
  }

  /**
   * Queue and play an audio chunk (PCM16 data from Gemini)
   */
  async playChunk(pcmData: ArrayBuffer): Promise<void> {
    // Initialize if needed
    if (!this.audioContext) {
      await this.initialize();
    }

    if (!this.audioContext) {
      console.error('AudioStreamPlayer: No audio context');
      return;
    }

    // Resume context if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    try {
      // Convert PCM16 to Float32
      const pcm16 = new Int16Array(pcmData);
      const float32 = new Float32Array(pcm16.length);

      for (let i = 0; i < pcm16.length; i++) {
        // Normalize to [-1, 1] range
        float32[i] = pcm16[i] / 32768;
      }

      // Create audio buffer
      const audioBuffer = this.audioContext.createBuffer(
        1, // mono
        float32.length,
        this.sampleRate
      );
      audioBuffer.getChannelData(0).set(float32);

      // Create source node
      const sourceNode = this.audioContext.createBufferSource();
      sourceNode.buffer = audioBuffer;
      sourceNode.connect(this.audioContext.destination);

      // Calculate start time for seamless playback
      const currentTime = this.audioContext.currentTime;

      // If this is the first chunk or we've fallen behind, reset timing
      if (!this.isPlaying || this.nextStartTime < currentTime) {
        // Add small buffer to prevent immediate playback issues
        this.nextStartTime = currentTime + this.minBufferTime;

        if (!this.isPlaying) {
          this.isPlaying = true;
          this.callbacks.onPlaybackStart?.();
        }
      }

      // Schedule playback at the precise time
      sourceNode.start(this.nextStartTime);

      // Track this source node
      this.pendingBuffers.push(sourceNode);

      // Update next start time for the following chunk
      this.nextStartTime += audioBuffer.duration;

      // Clean up when finished
      sourceNode.onended = () => {
        const index = this.pendingBuffers.indexOf(sourceNode);
        if (index > -1) {
          this.pendingBuffers.splice(index, 1);
        }

        // Check if all buffers finished
        if (this.pendingBuffers.length === 0) {
          // Add small delay to check if more audio is coming
          setTimeout(() => {
            if (this.pendingBuffers.length === 0 && this.isPlaying) {
              this.isPlaying = false;
              this.callbacks.onPlaybackEnd?.();
            }
          }, 100);
        }
      };

    } catch (error) {
      console.error('AudioStreamPlayer: Playback error:', error);
      this.callbacks.onError?.(
        new Error(`Audio playback error: ${error instanceof Error ? error.message : 'Unknown error'}`)
      );
    }
  }

  /**
   * Stop all pending audio and clear the queue
   */
  stop(): void {
    // Stop all pending source nodes
    for (const source of this.pendingBuffers) {
      try {
        source.stop();
        source.disconnect();
      } catch {
        // Ignore errors if already stopped
      }
    }
    this.pendingBuffers = [];

    // Reset timing
    this.nextStartTime = 0;

    if (this.isPlaying) {
      this.isPlaying = false;
      this.callbacks.onPlaybackEnd?.();
    }
  }

  /**
   * Check if currently playing
   */
  get playing(): boolean {
    return this.isPlaying;
  }

  /**
   * Get number of pending buffers
   */
  get queueLength(): number {
    return this.pendingBuffers.length;
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    this.stop();

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
